import React, { useContext } from 'react';
import { Button, Flex, Modal } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { Expense } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Currency from '@totem/components/ui_components/widgets/currency/Currency';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import {
  formatCurrency,
  formatDateOnly,
  isNotNull,
} from '@totem/utilities/common';
import {
  sortBooleanAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  expenses: Expense[];
};

const ExpensesTable = ({ expenses }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { onAction } = useContext(TicketContext);

  const columns: ColumnProps<Expense>[] = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (_, record: Expense) => record.category,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (_, record: Expense) => record.description,
    },
    {
      title: 'Expense Date',
      dataIndex: 'expenseDate',
      key: 'expenseDate',
      render: (_, record: Expense) => formatDateOnly(record.expenseDate),
      sorter: (compA, compB) =>
        sortStringAscending(compA.expenseDate, compB.expenseDate),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Amount',
      dataIndex: 'expenseDate',
      key: 'expenseDate',
      render: (_, record: Expense) => (
        <Currency
          currency={record.currency}
          amount={record.amount}
          showCurrency
        />
      ),
      sorter: (compA, compB) =>
        sortStringAscending(compA.expenseDate, compB.expenseDate),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Billable',
      dataIndex: 'billable',
      key: 'billable',
      render: (_, record: Expense) =>
        record.billable ? <span>Yes</span> : <span>No</span>,
      sorter: (compA, compB) =>
        sortBooleanAscending(compA.billable, compB.billable),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: '100px',
      render: (_, record: Expense) => {
        if (record.createdBy === userProfile.email) {
          return (
            <Flex justify={'flex-end'} align={'center'}>
              <Button
                shape={'circle'}
                title={'Edit Expense Entry'}
                onClick={() => {
                  onAction('show-expense-entry', JSON.stringify(record));
                }}
                icon={<IBDIcon name={'Edit'} />}
              />
              <Button
                shape={'circle'}
                title={'Delete Expense Entry'}
                onClick={() => {
                  Modal.confirm({
                    title: 'Are you sure?',
                    content: `Please confirm that you would like to delete expense ${record.description} for ${formatCurrency(record.currency, record.amount)}.`,
                    okType: 'danger',
                    cancelText: 'Cancel',
                    open: false,
                    onOk: () => onAction('delete-expense-entry', record.id),
                  });
                }}
                icon={<IBDIcon name={'Delete'} />}
              />
            </Flex>
          );
        }

        return <span />;
      },
    },
  ];

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={isNotNull(expenses) ? expenses : []}
        loading={false}
        rowKey="id"
      />
    </>
  );
};

export default ExpensesTable;
